import React, { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';
import toast from 'react-hot-toast';

const NAVY = '#1B2A6B';
const GOLD = '#C9A84C';

function getStrength(pw) {
  if (!pw) return null;
  const hasUpper = /[A-Z]/.test(pw);
  const hasNum   = /[0-9]/.test(pw);
  const hasSpec  = /[^A-Za-z0-9]/.test(pw);
  const len = pw.length;
  if (len < 8)                    return { label: 'Weak',   color: '#ef4444', w: '25%' };
  if (!hasUpper || !hasNum)       return { label: 'Fair',   color: '#f97316', w: '50%' };
  if (len < 12)                   return { label: 'Good',   color: '#eab308', w: '75%' };
  if (hasUpper && hasNum && hasSpec) return { label: 'Strong', color: '#22c55e', w: '100%' };
  return { label: 'Good', color: '#eab308', w: '75%' };
}

function PwInput({ value, onChange, placeholder }) {
  const [show, setShow] = useState(false);
  return (
    <div style={{ position: 'relative' }}>
      <input type={show ? 'text' : 'password'} value={value} onChange={onChange}
        placeholder={placeholder || '••••••••'}
        style={{ width: '100%', padding: '11px 14px', paddingRight: 44, border: '1.5px solid #e2e8f0', borderRadius: 10, fontSize: 14, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' }}
        onFocus={e => e.target.style.borderColor = NAVY}
        onBlur={e => e.target.style.borderColor = '#e2e8f0'}
      />
      <button type="button" onClick={() => setShow(s => !s)}
        style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 16 }}>
        {show ? '🙈' : '👁️'}
      </button>
    </div>
  );
}

export default function ResetPasswordPage() {
  const { token } = useParams();
  const navigate  = useNavigate();
  const [form, setForm]       = useState({ new_password: '', confirm_password: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError]     = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const strength = getStrength(form.new_password);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.new_password.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (!/[A-Z]/.test(form.new_password)) { setError('Password must contain at least one uppercase letter'); return; }
    if (!/[0-9]/.test(form.new_password)) { setError('Password must contain at least one number'); return; }
    if (form.new_password !== form.confirm_password) { setError('Passwords do not match'); return; }

    setLoading(true);
    try {
      await authAPI.resetPassword(token, { new_password: form.new_password, confirm_password: form.confirm_password });
      setSuccess(true);
      toast.success('Password reset successfully!');
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      setError(err.response?.data?.message || 'Reset failed. The link may have expired.');
    } finally { setLoading(false); }
  };

  return (
    <div style={{ minHeight: '100vh', background: `linear-gradient(135deg, ${NAVY} 0%, #13204f 50%, #0d1534 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <div style={{ position: 'fixed', top: -80, right: -80, width: 320, height: 320, borderRadius: '50%', background: 'rgba(201,168,76,0.08)', pointerEvents: 'none' }} />

      <div style={{ background: 'rgba(255,255,255,0.98)', borderRadius: 20, padding: '40px 36px', width: '100%', maxWidth: 400, boxShadow: '0 25px 80px rgba(0,0,0,0.3)' }}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{ width: 56, height: 56, background: `linear-gradient(135deg, ${NAVY}, #2d4299)`, borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
            <span style={{ fontSize: 24, fontWeight: 900, color: GOLD }}>N</span>
          </div>
          <h1 style={{ fontSize: 20, fontWeight: 800, color: NAVY, marginBottom: 4 }}>Set New Password</h1>
          <p style={{ fontSize: 13, color: '#64748b' }}>Choose a strong password for your account</p>
        </div>

        {success ? (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 56, marginBottom: 14 }}>✅</div>
            <h2 style={{ fontSize: 18, fontWeight: 800, color: '#15803d', marginBottom: 8 }}>Password Reset Successful!</h2>
            <p style={{ fontSize: 14, color: '#64748b', marginBottom: 20 }}>Redirecting to login…</p>
            <Link to="/login" style={{ display: 'inline-block', padding: '11px 24px', background: NAVY, color: '#fff', borderRadius: 10, fontWeight: 700, textDecoration: 'none', fontSize: 14 }}>
              Go to Login
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gap: 16 }}>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  New Password
                </label>
                <PwInput value={form.new_password} onChange={e => set('new_password', e.target.value)} placeholder="Min 8 chars, uppercase, number" />
                {strength && (
                  <div style={{ marginTop: 6 }}>
                    <div style={{ height: 4, background: '#f1f5f9', borderRadius: 4, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: strength.w, background: strength.color, transition: 'width 0.3s', borderRadius: 4 }} />
                    </div>
                    <span style={{ fontSize: 11, color: strength.color, fontWeight: 700 }}>{strength.label}</span>
                  </div>
                )}
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Confirm Password
                </label>
                <PwInput value={form.confirm_password} onChange={e => set('confirm_password', e.target.value)} />
                {form.confirm_password && form.new_password !== form.confirm_password && (
                  <p style={{ fontSize: 11, color: '#ef4444', marginTop: 3 }}>Passwords do not match</p>
                )}
              </div>

              {error && (
                <div style={{ background: '#fee2e2', color: '#dc2626', padding: '10px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600 }}>
                  {error === 'Reset link is invalid or has expired. Please request a new one.' ? (
                    <>
                      {error}{' '}
                      <Link to="/forgot-password" style={{ color: '#dc2626', fontWeight: 800 }}>Request new link →</Link>
                    </>
                  ) : error}
                </div>
              )}

              <button type="submit" disabled={loading}
                style={{
                  padding: '12px', borderRadius: 10, border: 'none',
                  background: loading ? '#94a3b8' : `linear-gradient(135deg, ${NAVY}, #2d4299)`,
                  color: '#fff', fontSize: 15, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer',
                  fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                }}>
                {loading && <div style={{ width: 18, height: 18, border: '2.5px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />}
                {loading ? 'Resetting…' : 'Reset Password'}
              </button>

              <div style={{ textAlign: 'center' }}>
                <Link to="/login" style={{ fontSize: 13, color: '#64748b', textDecoration: 'none' }}>← Back to Login</Link>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
